import React, { useState, useEffect } from 'react';
import { db } from './firebase';
import { collection, addDoc, getDocs, deleteDoc, doc } from 'firebase/firestore';

const AdminManagement = ({ onBack }) => {
    const [admins, setAdmins] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [showForm, setShowForm] = useState(false);
    const [formData, setFormData] = useState({
        email: '',
        password: '',
        location: '',
        telegramChatId: ''
    });

    // Fetch admins from Firestore
    const fetchAdmins = async () => {
        try {
            setLoading(true);
            const querySnapshot = await getDocs(collection(db, 'newadmins'));
            const adminsList = [];
            querySnapshot.forEach((doc) => {
                adminsList.push({ id: doc.id, ...doc.data() });
            });
            setAdmins(adminsList);
        } catch (err) {
            console.error('Error fetching admins:', err);
            setError('Adminlarni yuklashda xatolik.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAdmins();
    }, []);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    /**
     * Creates a new admin in Firestore
     * @param {Object} adminData - Admin data object
     * @param {string} adminData.email - Admin email (required)
     * @param {string} adminData.password - Admin password (required, min 6 characters)
     * @param {string} adminData.location - Admin location (required)
     * @param {string} adminData.telegramChatId - Telegram Chat ID (optional)
     * @returns {Promise<Object>} - Created admin document data
     * @throws {Error} - If validation fails or creation error occurs
     */
    const createNewAdmin = async (adminData) => {
        // Validate required fields
        if (!adminData.email || !adminData.password || !adminData.location) {
            throw new Error('Email, parol va lokatsiya majburiy maydonlar!');
        }

        // Validate email format
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(adminData.email)) {
            throw new Error('Email formati noto\'g\'ri.');
        }

        // Validate password length
        if (adminData.password.length < 6) {
            throw new Error('Parol kamida 6 ta belgi bo\'lishi kerak.');
        }

        // Validate password strength (optional - can be enhanced)
        if (adminData.password.length > 50) {
            throw new Error('Parol juda uzun (maksimum 50 belgi).');
        }

        // Normalize email
        const normalizedEmail = adminData.email.toLowerCase().trim();
        
        // Validate location is not empty after trim
        const normalizedLocation = adminData.location.trim();
        if (!normalizedLocation) {
            throw new Error('Lokatsiya bo\'sh bo\'lishi mumkin emas.');
        }

        // Check if email already exists
        const existingAdmins = await getDocs(collection(db, 'newadmins'));
        let emailExists = false;
        existingAdmins.forEach((doc) => {
            if (doc.data().email === normalizedEmail) {
                emailExists = true;
            }
        });

        if (emailExists) {
            throw new Error('Bu email allaqachon ishlatilgan.');
        }

        // Prepare admin data for Firestore
        const adminDoc = {
            email: normalizedEmail,
            password: adminData.password, // Store password in Firestore
            location: normalizedLocation,
            telegramChatId: (adminData.telegramChatId || '').trim(),
            createdAt: new Date(),
            createdBy: localStorage.getItem('userRole') === 'superadmin' 
                ? 'superadmin' 
                : 'system'
        };

        // Create admin in Firestore
        const docRef = await addDoc(collection(db, 'newadmins'), adminDoc);
        
        return {
            id: docRef.id,
            ...adminDoc
        };
    };

    /**
     * Handler for form submission to create new admin
     */
    const handleCreateAdmin = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        setLoading(true);

        try {
            // Create admin using the createNewAdmin function
            const newAdmin = await createNewAdmin({
                email: formData.email,
                password: formData.password,
                location: formData.location,
                telegramChatId: formData.telegramChatId
            });

            setSuccess(`Admin "${newAdmin.email}" muvaffaqiyatli yaratildi!`);
            setFormData({ email: '', password: '', location: '', telegramChatId: '' });
            setShowForm(false);
            fetchAdmins();
        } catch (err) {
            console.error('Error creating admin:', err);
            setError(err.message || `Xatolik: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteAdmin = async (adminId, adminEmail) => {
        if (window.confirm(`"${adminEmail}" adminni o'chirmoqchimisiz?`)) {
            try {
                setLoading(true);
                await deleteDoc(doc(db, 'newadmins', adminId));
                setSuccess(`Admin "${adminEmail}" o'chirildi.`);
                fetchAdmins();
            } catch (err) {
                console.error('Error deleting admin:', err);
                setError('Adminni o\'chirishda xatolik.');
            } finally {
                setLoading(false);
            }
        }
    };

    const styles = `
    .admin-management {
      max-width: 900px;
      margin: 40px auto;
      padding: 20px;
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
    }
    .header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 24px;
      padding: 20px;
      padding-bottom: 16px;
      border-bottom: 2px solid #e1e4e8;
      background: #ffffff;
      border-radius: 8px;
      box-shadow: 0 2px 4px rgba(0,0,0,0.05);
    }
    .header h1 {
      font-size: 24px;
      color: #24292e;
      margin: 0;
      flex: 1;
    }
    @media (max-width: 768px) {
      .header {
        flex-direction: column;
        align-items: flex-start;
        gap: 15px;
      }
      .header h1 {
        width: 100%;
      }
    }
    .btn {
      padding: 10px 20px;
      border-radius: 6px;
      font-size: 14px;
      font-weight: 600;
      cursor: pointer;
      border: 1px solid rgba(27,31,35,0.15);
      transition: all 0.2s ease;
      display: inline-block;
      text-align: center;
      min-width: 120px;
    }
    .btn:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
    .btn-primary { 
      background-color: #2ea44f; 
      color: white; 
      border-color: #2ea44f;
    }
    .btn-primary:hover { 
      background-color: #2c974b; 
      border-color: #2c974b;
    }
    .btn-secondary { 
      background-color: #fafbfc; 
      color: #24292e; 
      border-color: #d1d5da; 
    }
    .btn-secondary:hover {
      background-color: #f3f4f6;
    }
    .btn-danger { 
      background-color: #d73a49; 
      color: white; 
      border-color: #d73a49;
    }
    .btn-danger:hover { 
      background-color: #cb2431; 
      border-color: #cb2431;
    }
    .form-container {
      background: #f6f8fa;
      padding: 24px;
      border-radius: 6px;
      border: 1px solid #e1e4e8;
      margin-bottom: 24px;
    }
    .form-group {
      margin-bottom: 16px;
    }
    .form-group label {
      display: block;
      font-weight: 600;
      margin-bottom: 6px;
      font-size: 14px;
    }
    .form-group input {
      width: 100%;
      padding: 8px 12px;
      border: 1px solid #d1d5da;
      border-radius: 6px;
      font-size: 14px;
      box-sizing: border-box;
    }
    .form-group input:focus {
      border-color: #0366d6;
      outline: none;
      box-shadow: 0 0 0 3px rgba(3,102,214,0.1);
    }
    .form-buttons {
      display: flex;
      gap: 10px;
      margin-top: 20px;
    }
    .admins-table {
      width: 100%;
      border-collapse: collapse;
      background: white;
      border: 1px solid #e1e4e8;
      border-radius: 6px;
      overflow: hidden;
    }
    .admins-table th, .admins-table td {
      padding: 12px;
      text-align: left;
      border-bottom: 1px solid #e1e4e8;
    }
    .admins-table th {
      background: #f6f8fa;
      font-weight: 600;
      font-size: 12px;
      text-transform: uppercase;
    }
    .error { background: #ffeef0; color: #d73a49; padding: 12px; border-radius: 6px; margin-bottom: 16px; }
    .success { background: #dcffe4; color: #22863a; padding: 12px; border-radius: 6px; margin-bottom: 16px; }
  `;

    return (
        <>
            <style>{styles}</style>
            <div className="admin-management">
                <div className="header">
                    <h1>🛡️ Admin Boshqaruvi</h1>
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                        <button 
                            className="btn btn-primary" 
                            onClick={() => setShowForm(!showForm)}
                            disabled={loading}
                            style={{ 
                                fontSize: '15px',
                                padding: '12px 24px',
                                fontWeight: 'bold'
                            }}
                        >
                            {showForm ? '✕ Bekor qilish' : '+ Yangi Admin Yaratish'}
                        </button>
                        <button 
                            className="btn btn-secondary" 
                            onClick={onBack}
                            disabled={loading}
                        >
                            ← Orqaga
                        </button>
                    </div>
                </div>

                {error && <div className="error">{error}</div>}
                {success && <div className="success">{success}</div>}

                {/* Prominent Create Admin Button - Always Visible */}
                {!showForm && (
                    <div style={{ 
                        marginBottom: '24px', 
                        padding: '16px', 
                        background: '#f0f9ff', 
                        border: '2px solid #2ea44f', 
                        borderRadius: '8px',
                        textAlign: 'center'
                    }}>
                        <button 
                            className="btn btn-primary" 
                            onClick={() => setShowForm(true)}
                            disabled={loading}
                            style={{ 
                                fontSize: '16px',
                                padding: '14px 28px',
                                fontWeight: 'bold',
                                minWidth: '200px'
                            }}
                        >
                            ➕ Yangi Admin Yaratish
                        </button>
                        <p style={{ marginTop: '10px', color: '#666', fontSize: '14px' }}>
                            Yangi admin yaratish uchun tugmani bosing
                        </p>
                    </div>
                )}

                {showForm && (
                    <div className="form-container">
                        <h2>Yangi Admin Yaratish</h2>
                        <form onSubmit={handleCreateAdmin}>
                            <div className="form-group">
                                <label>Email *</label>
                                <input
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    placeholder="admin@example.com"
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label>Parol * (kamida 6 ta belgi)</label>
                                <input
                                    type="password"
                                    name="password"
                                    value={formData.password}
                                    onChange={handleInputChange}
                                    placeholder="••••••"
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label>Lokatsiya *</label>
                                <input
                                    type="text"
                                    name="location"
                                    value={formData.location}
                                    onChange={handleInputChange}
                                    placeholder="Samarqand"
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label>Telegram Chat ID</label>
                                <input
                                    type="text"
                                    name="telegramChatId"
                                    value={formData.telegramChatId}
                                    onChange={handleInputChange}
                                    placeholder="-1001234567890"
                                />
                            </div>
                            <div className="form-buttons">
                                <button type="submit" className="btn btn-primary" disabled={loading}>
                                    {loading ? 'Yaratilmoqda...' : 'Admin Yaratish'}
                                </button>
                            </div>
                        </form>
                    </div>
                )}

                <h2>Adminlar Ro'yxati ({admins.length})</h2>
                {loading && <p>Yuklanmoqda...</p>}
                {!loading && admins.length === 0 ? (
                    <p style={{ color: '#666' }}>Adminlar topilmadi. Yangi admin yarating.</p>
                ) : (
                    <table className="admins-table">
                        <thead>
                            <tr>
                                <th>Email</th>
                                <th>Lokatsiya</th>
                                <th>Telegram Chat ID</th>
                                <th>Yaratilgan</th>
                                <th>Harakatlar</th>
                            </tr>
                        </thead>
                        <tbody>
                            {admins.map((admin) => (
                                <tr key={admin.id}>
                                    <td>{admin.email}</td>
                                    <td>{admin.location}</td>
                                    <td>{admin.telegramChatId || '-'}</td>
                                    <td>{admin.createdAt?.toDate?.().toLocaleDateString('uz-UZ') || 'N/A'}</td>
                                    <td>
                                        <button
                                            className="btn btn-danger"
                                            onClick={() => handleDeleteAdmin(admin.id, admin.email)}
                                            disabled={loading}
                                        >
                                            O'chirish
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </>
    );
};

export default AdminManagement;
